import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, CalendarRange, ChevronDown, ChevronRight, ChevronUp, Download, Loader2, Paperclip,
  Plus, RefreshCw, RotateCcw, Search, StickyNote, Trash2, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { erpAPI } from '../../services/api';
import Modal from '../../components/admin/Modal';
import Toast from '../../components/admin/Toast';
import SearchableSelect from '../../components/admin/SearchableSelect';
import { paymentAccountOptions } from '../../utils/erpHelpers';
import './ERPManagement.css';
import './Accounts.css';
import './PurchaseManagement.css';

type Mode = 'add' | 'history' | 'suppliers';
type Props = { mode: Mode };

const money = (v: any) => `\u09F3${Number(v || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (v: any) => v ? new Date(v).toLocaleDateString('en-BD') : '-';
const dt = (v: any) => v ? new Date(v).toLocaleString('en-BD') : '-';
const rows = (p: any): any[] => Array.isArray(p) ? p : (p?.results || []);
const errorText = (e: any) => {
  const d = e?.response?.data;
  if (typeof d === 'string') return d;
  if (d?.detail) return d.detail;
  if (d && typeof d === 'object') return Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ');
  return e?.message || 'Request failed';
};

type DrawerTab = 'summary' | 'items' | 'pricing' | 'payments' | 'timeline' | 'accounting' | 'documents';

type InvoiceRow = {
  key: string;
  product_variant?: number;
  product: number;
  product_name: string;
  product_code: string;
  sku: string;
  color: string;
  size: string;
  brand: number | null;
  quantity: number;
  unit_cost: number;
  wholesale_price: number;
  selling_price: number;
  current_stock?: number | null;
  last_purchase_cost?: number | null;
};

type SearchEntry =
  | { _type: 'product'; id: number; name_en: string; name_bn: string; sku: string }
  | {
      _type: 'variant'; id: number; product: number; product_name: string; product_code: string;
      sku: string; color: string; size: string; unit_cost: number; wholesale_price: number;
      selling_price: number; current_stock?: number | null; last_purchase_cost?: number | null;
    };

export default function PurchaseManagement({ mode }: Props) {
  const navigate = useNavigate();
  const [me, setMe] = useState<any>(null);
  const [locationFilter, setLocationFilter] = useState<string>('');
  const perms = useMemo(() => new Set(me?.permissions || []), [me]);
  const can = useCallback((code: string) => Boolean(me?.is_superuser || me?.is_admin || perms.has(code)), [me, perms]);

  useEffect(() => {
    erpAPI.me().then(r => setMe(r.data)).catch(() => {});
    const stored = sessionStorage.getItem('erp-location-filter') || '';
    setLocationFilter(stored);
    const change = (event: Event) => {
      const next = String((event as CustomEvent).detail?.location || '');
      sessionStorage.setItem('erp-location-filter', next);
      setLocationFilter(next);
    };
    window.addEventListener('erp-location-change', change);
    return () => window.removeEventListener('erp-location-change', change);
  }, []);

  const [locations, setLocations] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    Promise.all([
      erpAPI.list('locations', {}).then(r => setLocations(rows(r.data))),
      erpAPI.list('suppliers', {}).then(r => setSuppliers(rows(r.data))),
      erpAPI.list('payment-accounts', {}).then(r => setPaymentMethods(rows(r.data))).catch(() => {}),
    ]).catch(() => {}).finally(() => setReady(true));
  }, []);

  return (
    <div className="pm-page">
      <div className="pm-head">
        <button className="pm-back" onClick={() => navigate('/admin/operations')}><ArrowLeft size={18} /></button>
        <div>
          <h1>{mode === 'add' ? 'Add Purchase' : mode === 'history' ? 'Purchase History' : 'Manage Suppliers'}</h1>
          <p className="pm-subtitle">
            {mode === 'add'
              ? 'Search a variant, set quantities and prices, then post. Stock, bill, and journal update automatically.'
              : mode === 'history'
                ? 'Review purchases, payables, payments, and audit history.'
                : 'Supplier phone number is the unique business identifier.'}
          </p>
        </div>
        {mode === 'add' && (
          <div className="pm-head-actions">
            <button className="pm-btn pm-btn-secondary" onClick={() => navigate('/admin/purchases/history')}><RotateCcw size={14} /> History</button>
          </div>
        )}
      </div>

      {!ready ? (
        <div className="erp-loading"><Loader2 size={20} className="spin" /> Loading purchase workspace…</div>
      ) : (
        <>
          {mode === 'add' && <AddPurchaseWorkspace locations={locations} suppliers={suppliers} locationFilter={locationFilter} />}
          {mode === 'history' && <HistoryWorkspace can={can} locations={locations} suppliers={suppliers} paymentMethods={paymentMethods} locationFilter={locationFilter} />}
          {mode === 'suppliers' && <SuppliersWorkspace suppliers={suppliers} onChanged={() => erpAPI.list('suppliers', {}).then(r => setSuppliers(rows(r.data)))} />}
        </>
      )}
    </div>
  );
}

/* ══════════════════════════ HISTORY WORKSPACE ══════════════════════════ */

function HistoryWorkspace({ can, locations, suppliers, paymentMethods, locationFilter }: any) {
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, any> = { page, page_size: pageSize };
    if (filters.search) p.search = filters.search;
    if (filters.supplier) p.supplier = filters.supplier;
    if (filters.status) p.status = filters.status;
    if (filters.start) p.start = filters.start;
    if (filters.end) p.end = filters.end;
    if (locationFilter) p.location = locationFilter;
    return p;
  }, [filters, page, pageSize, locationFilter]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      erpAPI.list('purchases', params).then(r => {
        setData(rows(r.data));
        setTotal(r.data?.count || rows(r.data).length);
      }),
      erpAPI.list('purchases/summary', params).then(r => setSummary(r.data)).catch(() => setSummary(null)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const locName = (id: number) => locations.find((l: any) => l.id === id)?.name || '-';
  const showLoc = useMemo(() => !locationFilter, [locationFilter]);

  const clearFilters = () => { setFilters({}); setPage(1); };

  const kpis = useMemo(() => summary || { total_purchases: 0, purchase_value: 0, total_paid: 0, outstanding_payable: 0 }, [summary]);

  const openDrawer = async (item: any) => {
    try {
      const resp = await erpAPI.get('purchases', item.id);
      setSelected(resp.data);
      setDrawerOpen(true);
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return <div className="erp-page">
    <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />

    {/* KPI Cards */}
    <div className="acct-kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
      <div className="erp-stat"><span>Total Purchases</span><strong style={{ color: '#2563eb' }}>{kpis.total_purchases}</strong></div>
      <div className="erp-stat"><span>Purchase Value</span><strong style={{ color: '#7c3aed' }}>{money(kpis.purchase_value)}</strong></div>
      <div className="erp-stat"><span>Paid Amount</span><strong style={{ color: '#059669' }}>{money(kpis.total_paid)}</strong></div>
      <div className="erp-stat"><span>Outstanding Payables</span><strong style={{ color: '#dc2626' }}>{money(kpis.outstanding_payable)}</strong></div>
    </div>

    {/* Filter bar */}
    <div className="pm-filter">
      <div className="pm-filter-search-box">
        <Search size={14} />
        <input value={filters.search || ''} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
          placeholder="Search purchase no, supplier, invoice, SKU, product…" />
        {filters.search && <button className="pm-filter-ico" onClick={() => { setFilters(f => ({ ...f, search: '' })); setPage(1); }}><X size={13} /></button>}
      </div>

      <div className="pm-filter-control">
        <SearchableSelect value={filters.supplier || ''} onChange={n => { setFilters(f => ({ ...f, supplier: n })); setPage(1); }}
          placeholder="Supplier" options={suppliers.map((s: any) => ({ value: s.id, label: s.name }))} />
      </div>

      <div className="pm-filter-control">
        <SearchableSelect value={filters.status || ''} onChange={n => { setFilters(f => ({ ...f, status: n })); setPage(1); }}
          placeholder="All statuses" options={['COMPLETED', 'DRAFT', 'CANCELLED'].map(s => ({ value: s, label: s }))} />
      </div>

      <div className="pm-filter-dates">
        <span className="pm-filter-dates-label"><CalendarRange size={14} /></span>
        <input type="date" value={filters.start || ''} onChange={e => { setFilters(f => ({ ...f, start: e.target.value })); setPage(1); }} title="From date" />
        <em>–</em>
        <input type="date" value={filters.end || ''} onChange={e => { setFilters(f => ({ ...f, end: e.target.value })); setPage(1); }} title="To date" />
      </div>

      <div className="pm-filter-actions">
        {Object.keys(filters).some(k => filters[k]) && (
          <button className="pm-filter-reset" onClick={clearFilters}><RotateCcw size={12} /> Reset</button>
        )}
        <button className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => exportPurchases(data)}><Download size={12} /> Export</button>
      </div>
    </div>

    {/* Table */}
    {loading ? <div className="erp-loading"><RefreshCw className="spin" /> Loading purchases…</div> :
      <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="erp-table-wrap" style={{ maxHeight: '52vh', overflowY: 'auto' }}>
          <table style={{ position: 'relative' }}>
            <thead><tr style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
              <th>Purchase #</th>{showLoc && <th>Destination</th>}<th>Date</th><th>Supplier</th><th>Items</th><th>Total</th><th>Paid</th><th>Due</th><th>Status</th><th>Created By</th>
            </tr></thead>
            <tbody>{data.length === 0 ? <tr><td className="erp-empty" colSpan={showLoc ? 10 : 9}>No purchases found.</td></tr> :
              data.map((r: any) => <tr key={r.id}
                onClick={() => openDrawer(r)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ fontWeight: 600, fontSize: '.78rem' }}>{r.purchase_number}</td>
                {showLoc && <td style={{ fontSize: '.75rem', color: '#4f46e5', fontWeight: 600 }}>{r.destination_name || locName(r.destination)}</td>}
                <td style={{ fontSize: '.72rem' }}>{date(r.purchase_date)}</td>
                <td><strong style={{ fontSize: '.8rem' }}>{r.supplier_name || '-'}</strong></td>
                <td style={{ fontSize: '.75rem', color: '#64748b' }}>{r.items_count ?? 0}</td>
                <td style={{ fontWeight: 700 }}>{money(r.total_amount)}</td>
                <td style={{ color: '#059669' }}>{money(r.paid_amount)}</td>
                <td style={{ fontWeight: 600, color: Number(r.due_amount) > 0 ? '#dc2626' : '#059669' }}>{money(r.due_amount)}</td>
                <td><PurchaseStatusBadge status={r.status} billStatus={r.bill_status} /></td>
                <td style={{ fontSize: '.72rem', color: '#64748b' }}>{r.created_by_name || '-'}</td>
              </tr>)}</tbody>
          </table>
        </div>
      </section>}

    {/* Pagination */}
    <div className="pm-pagination">
      <span>{total} purchase{total !== 1 ? 's' : ''}</span>
      <div className="pm-pagination-btns">
        <button className="pm-btn pm-btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
        <span>Page {page} of {totalPages}</span>
        <button className="pm-btn pm-btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
      </div>
    </div>

    <PurchaseDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} purchase={selected}
      can={can} locations={locations} paymentMethods={paymentMethods} locName={locName} onReload={load} />
  </div>;
}

function exportPurchases(data: any[]) {
  const header = ['Purchase #', 'Date', 'Supplier', 'Destination', 'Items', 'Total', 'Paid', 'Due', 'Status', 'Created By'];
  const lines = data.map(r => [r.purchase_number, r.purchase_date, r.supplier_name, r.destination_name, r.items_count, r.total_amount, r.paid_amount, r.due_amount, r.status, r.created_by_name]);
  const csv = [header, ...lines].map(row => row.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'purchases.csv'; a.click();
  URL.revokeObjectURL(url);
}

function PurchaseStatusBadge({ status, billStatus }: { status: string; billStatus?: string }) {
  const cls = status === 'COMPLETED' ? 'received' : status === 'CANCELLED' ? 'cancelled' : '';
  const billCls = billStatus === 'PAID' ? 'received' : billStatus === 'PARTIAL' ? 'dispatched' : billStatus === 'UNPAID' ? 'cancelled' : '';
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <span className={`erp-status ${cls}`}>{status}</span>
    {billStatus && <span className={`erp-status ${billCls}`} style={{ background: 'transparent', padding: 0, fontSize: '.62rem' }}>{billStatus}</span>}
  </div>;
}

/* ══════════════════════════ PURCHASE DETAIL DRAWER ══════════════════════════ */

function PurchaseDrawer({ open, onClose, purchase, can, locations, paymentMethods, locName, onReload }: any) {
  const [tab, setTab] = useState<DrawerTab>('summary');
  const [detail, setDetail] = useState<any>(purchase);
  const [payModal, setPayModal] = useState(false);
  const [payForm, setPayForm] = useState<Record<string, any>>({});
  const [paying, setPaying] = useState(false);
  const [scopedMethods, setScopedMethods] = useState<any[]>(paymentMethods);
  const [returnModal, setReturnModal] = useState(false);
  const [returnItems, setReturnItems] = useState<Record<number, number>>({});
  const [returnReasons, setReturnReasons] = useState<any[]>([]);
  const [returnReasonId, setReturnReasonId] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [returning, setReturning] = useState(false);
  const [returnsList, setReturnsList] = useState<any[]>([]);
  const [preview, setPreview] = useState<any>(null);
  const [previewing, setPreviewing] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteVisibility, setNoteVisibility] = useState('INTERNAL');
  const [newAttachment, setNewAttachment] = useState<any>(null);
  const [attachmentDesc, setAttachmentDesc] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) { setTab('summary'); setDetail(purchase); setMessage(null); }
  }, [open, purchase]);

  const loadReturns = useCallback(async (purchaseId: number) => {
    try {
      const res = await erpAPI.list('purchase-returns', { purchase: purchaseId });
      setReturnsList(rows(res.data));
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => {
    if (open && detail?.id) loadReturns(detail.id);
    if (open) {
      erpAPI.list('purchase-return-reasons', { is_active: true })
        .then(r => setReturnReasons(rows(r.data))).catch(() => {});
    }
  }, [open, detail?.id, loadReturns]);

  useEffect(() => {
    if (!returnModal || !detail?.id) { setPreview(null); return; }
    const items = (detail.items || []).filter((i: any) => Number(returnItems[i.id] || 0) > 0).map((i: any) => ({
      purchase_item: i.id,
      product_variant: i.product_variant,
      quantity: Number(returnItems[i.id]),
    }));
    if (!items.length) { setPreview(null); return; }
    let cancelled = false;
    setPreviewing(true);
    erpAPI.create('purchase-returns/preview', {
      supplier: detail.supplier,
      destination: detail.destination,
      purchase: detail.id,
      reason: Number(returnReasonId) || null,
      notes: returnNotes,
      items,
    }).then(res => { if (!cancelled) setPreview(res.data); })
      .catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setPreviewing(false); });
    return () => { cancelled = true; };
  }, [returnModal, returnItems, returnReasonId, returnNotes, detail?.id, detail?.supplier, detail?.destination]);

  if (!open || !detail) return null;

  const refresh = async () => {
    try {
      const resp = await erpAPI.get('purchases', detail.id);
      setDetail(resp.data);
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };

  const returnedMap: Record<number, number> = {};
  (returnsList || []).forEach(r => (r.items || []).forEach((it: any) => {
    if (it.purchase_item) returnedMap[it.purchase_item] = (returnedMap[it.purchase_item] || 0) + Number(it.quantity || 0);
  }));
  const returnTotal = (detail.items || []).reduce(
    (s: number, i: any) => s + (Number(returnItems[i.id] || 0) * Number(i.unit_cost || 0)), 0);
  const openReturnModal = () => {
    setReturnItems({}); setReturnReasonId(''); setReturnNotes(''); setPreview(null);
    setReturnModal(true);
  };

  const postReturn = async () => {
    const items = (detail.items || []).filter((i: any) => Number(returnItems[i.id] || 0) > 0).map((i: any) => ({
      purchase_item: i.id,
      product_variant: i.product_variant,
      quantity: Number(returnItems[i.id]),
    }));
    if (!items.length) { setMessage({ type: 'error', text: 'Select at least one item to return.' }); return; }
    if (!returnReasonId) { setMessage({ type: 'error', text: 'Select a return reason.' }); return; }
    setReturning(true);
    try {
      await erpAPI.create('purchase-returns', {
        supplier: detail.supplier,
        destination: detail.destination,
        purchase: detail.id,
        reason: Number(returnReasonId),
        notes: returnNotes,
        items,
      });
      setReturnModal(false);
      await Promise.all([refresh(), loadReturns(detail.id)]);
      onReload();
      setMessage({ type: 'success', text: 'Return posted. Payable/credit and inventory updated — no cash moved.' });
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setReturning(false); }
  };

  const bill = detail.bill || {};
  const due = Number(bill.due_amount ?? detail.total_amount - detail.paid_amount ?? 0);
  const paid = Number(bill.paid_amount ?? detail.paid_amount ?? 0);
  const TABS: DrawerTab[] = ['summary', 'items', 'pricing', 'payments', 'timeline', 'accounting', 'documents'];

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await erpAPI.action('purchases', detail.id, 'add_note', { note: newNote.trim(), visibility: noteVisibility });
      setNewNote('');
      await refresh();
      onReload();
      setMessage({ type: 'success', text: 'Note added.' });
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };

  const uploadAttachment = async () => {
    if (!newAttachment) return;
    const fd = new FormData();
    fd.append('file', newAttachment);
    fd.append('description', attachmentDesc);
    try {
      await erpAPI.action('purchases', detail.id, 'add_attachment', fd);
      setNewAttachment(null); setAttachmentDesc('');
      if (fileRef.current) fileRef.current.value = '';
      await refresh();
      onReload();
      setTab('documents');
      setMessage({ type: 'success', text: 'Attachment uploaded.' });
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };

  const handlePay = async () => {
    if (!payForm.method || !payForm.amount) return;
    if (Number(payForm.amount) <= 0 || Number(payForm.amount) > due) { setMessage({ type: 'error', text: 'Amount exceeds the outstanding payable.' }); return; }
    setPaying(true);
    try {
      await erpAPI.action('purchases', detail.id, 'pay', {
        amount: Number(payForm.amount), method: Number(payForm.method), reference: payForm.reference || '',
      });
      setPayModal(false); setPayForm({});
      await refresh();
      onReload();
      setMessage({ type: 'success', text: 'Payment recorded. Payable, journal, and cash account updated.' });
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setPaying(false); }
  };

  const methodLabel = (id: number) => paymentMethods.find((m: any) => m.id === id)?.name || '-';
  const methodAccount = (id: number) => paymentMethods.find((m: any) => m.id === id)?.cash_bank_account_name || '-';

  return <>
    <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />
    <div className="acct-modal-overlay" onClick={onClose}>
      <div className="acct-modal pm-drawer" onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header">
          <div>
            <h3 className="acct-modal-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              {detail.purchase_number}
              <PurchaseStatusBadge status={detail.status} billStatus={bill.status} />
            </h3>
            <span style={{ fontSize: '.75rem', color: '#64748b' }}>{detail.supplier_name} · {detail.destination_name || locName(detail.destination)}</span>
          </div>
          <button className="acct-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="acct-tabs pm-drawer-tabs">
          {TABS.map(t => <button key={t} className={`acct-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} style={{ textTransform: 'capitalize' }}>{t}</button>)}
        </div>

        <div className="acct-modal-body pm-drawer-body">
          {/* ══ Summary ══ */}
          {tab === 'summary' && <div>
            <div className="pm-summary-grid">
              <div><strong>Supplier</strong><span>{detail.supplier_name}</span><small>{detail.supplier_phone || '-'}</small></div>
              <div><strong>Destination</strong><span>{detail.destination_name || locName(detail.destination)}</span></div>
              <div><strong>Purchase Date</strong><span>{date(detail.purchase_date)}</span></div>
              <div><strong>Created By</strong><span>{detail.created_by_name || '-'}</span></div>
              <div><strong>Created At</strong><span>{dt(detail.created_at)}</span></div>
              <div><strong>Supplier Invoice</strong><span>{detail.invoice_number || '—'}</span></div>
              <div><strong>Bill Number</strong><span>{bill.bill_number || '—'}</span></div>
              <div><strong>Items</strong><span>{detail.items?.length || 0}</span></div>
            </div>
            <div className="pm-summary-totals">
              <div><strong>Total</strong><b>{money(detail.total_amount)}</b></div>
              <div><strong>Paid</strong><b style={{ color: '#059669' }}>{money(paid)}</b></div>
              <div><strong>Due</strong><b style={{ color: '#dc2626' }}>{money(due)}</b></div>
            </div>
            {detail.notes && <div className="pm-note-box"><strong>Notes</strong><p>{detail.notes}</p></div>}
          </div>}

          {/* ══ Items ══ */}
          {tab === 'items' && <ItemsTab items={detail.items} />}

          {/* ══ Pricing ══ */}
          {tab === 'pricing' && <PricingTab items={detail.items} />}

          {/* ══ Payments ══ */}
          {tab === 'payments' && <div>
            <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Payments</h4>
            {(!detail.payments || detail.payments.length === 0) ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No payments recorded yet.</div>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
                  <thead><tr style={{ background: '#f8fafc' }}>
                    <th style={thStyle}>Date</th><th style={thStyle}>Amount</th><th style={thStyle}>Source Account</th><th style={thStyle}>Method</th><th style={thStyle}>Reference</th><th style={thStyle}>Recorded By</th>
                  </tr></thead>
                  <tbody>{detail.payments.map((p: any, i: number) => <tr key={i}>
                    <td style={tdStyle}>{dt(p.paid_at)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{money(p.amount)}</td>
                    <td style={tdStyle}>{methodAccount(p.method) || '-'}</td>
                    <td style={tdStyle}><span style={{ padding: '2px 6px', borderRadius: 4, background: '#eff6ff', color: '#1d4ed8', fontSize: '.7rem', fontWeight: 600 }}>{p.method_name || methodLabel(p.method)}</span></td>
                    <td style={{ ...tdStyle, color: '#64748b' }}>{p.reference || '-'}</td>
                    <td style={tdStyle}>{p.created_by_name || '-'}</td>
                  </tr>)}</tbody>
                </table>
              </div>
            )}
            {can('purchase.create') && (
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button className="acct-btn acct-btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={openReturnModal}>
                  <RotateCcw size={14} /> Return Items
                </button>
                {Number(due) > 0 && (
                  <button className="acct-btn acct-btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => {
                      setPayForm({ amount: due, method: '', reference: '' });
                      // Scope the payment-account dropdown to the purchase's
                      // destination location (shared + destination accounts).
                      if (detail?.destination) {
                        erpAPI.list('payment-accounts', { location: detail.destination }).then(r => setScopedMethods(rows(r.data))).catch(() => {});
                      } else {
                        setScopedMethods(paymentMethods);
                      }
                      setPayModal(true);
                    }}>
                    Pay Bill ({money(due)})
                  </button>
                )}
              </div>
            )}

            {returnsList.length > 0 && <>
              <h4 style={{ margin: '16px 0 8px', fontSize: '.85rem' }}>Purchase Returns</h4>
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
                  <thead><tr style={{ background: '#f8fafc' }}>
                    <th style={thStyle}>Return #</th><th style={thStyle}>Date</th><th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Payable ↓</th><th style={{ ...thStyle, textAlign: 'right' }}>Supplier Credit</th><th style={thStyle}>Reason</th><th style={thStyle}>By</th>
                  </tr></thead>
                  <tbody>{returnsList.map((r: any, i: number) => <tr key={i}>
                    <td style={tdStyle}><strong>{r.return_number}</strong></td>
                    <td style={tdStyle}>{dt(r.created_at)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{money(r.total_amount)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#dc2626' }}>{money(r.payable_reduction)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#059669' }}>{money(r.supplier_receivable)}</td>
                    <td style={{ ...tdStyle, color: '#64748b', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason}>{r.reason || '—'}</td>
                    <td style={tdStyle}>{r.created_by_name || '-'}</td>
                  </tr>)}</tbody>
                </table>
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
                Returns reduce the outstanding payable first; already-paid value becomes a supplier receivable (1300). No cash is moved.
              </div>
            </>}
          </div>}

          {/* ══ Timeline ══ */}
          {tab === 'timeline' && <TimelineTab entries={detail.timeline} />}

          {/* ══ Accounting ══ */}
          {tab === 'accounting' && <AccountingTab purchase={detail} />}

          {/* ══ Documents ══ */}
          {tab === 'documents' && <div>
            <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}><StickyNote size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Notes</h4>
            <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add an internal or supplier-visible note…"
              style={{ width: '100%', minHeight: 56, border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, fontSize: '.78rem' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <select value={noteVisibility} onChange={e => setNoteVisibility(e.target.value)}
                style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', fontSize: '.72rem' }}>
                <option value="INTERNAL">Internal (staff only)</option>
                <option value="SUPPLIER">Supplier-visible</option>
              </select>
              <button onClick={addNote} className="acct-btn acct-btn-primary" style={{ padding: '6px 12px', fontSize: '.72rem' }}>Add Note</button>
            </div>
            {(detail.purchase_notes || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {detail.purchase_notes.map((n: any, i: number) => (
                  <div key={i} style={{ padding: '8px 10px', borderRadius: 8, background: n.visibility === 'INTERNAL' ? '#fffbeb' : '#f0fdf4', border: `1px solid ${n.visibility === 'INTERNAL' ? '#fef3c7' : '#bbf7d0'}`, fontSize: '.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 700, fontSize: '.66rem', textTransform: 'uppercase' }}>{n.visibility === 'INTERNAL' ? 'Internal' : 'Supplier-visible'}</span>
                      <span style={{ fontSize: '.66rem', color: '#64748b' }}>{n.created_by_name || 'Staff'} · {dt(n.created_at)}</span>
                    </div>
                    <div style={{ marginTop: 2, color: '#334155' }}>{n.note}</div>
                  </div>
                ))}
              </div>
            )}

            <h4 style={{ margin: '16px 0 8px', fontSize: '.85rem' }}><Paperclip size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Attachments</h4>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <input ref={fileRef} type="file" onChange={e => setNewAttachment(e.target.files?.[0] || null)} style={{ fontSize: '.72rem' }} />
              <input value={attachmentDesc} onChange={e => setAttachmentDesc(e.target.value)} placeholder="Description"
                style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: '.72rem' }} />
              <button onClick={uploadAttachment} disabled={!newAttachment} className="acct-btn acct-btn-primary" style={{ padding: '6px 12px', fontSize: '.72rem' }}>Upload</button>
            </div>
            {(detail.attachments || []).length === 0 ? (
              <div style={{ padding: 14, textAlign: 'center', color: '#94a3b8', fontSize: '.75rem' }}>No attachments.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {detail.attachments.map((a: any, i: number) => (
                  <div key={i} style={{ padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{a.filename}</div>
                      {a.description && <div style={{ color: '#64748b', fontSize: '.7rem' }}>{a.description}</div>}
                      <div style={{ color: '#94a3b8', fontSize: '.68rem' }}>{a.uploaded_by_name || '-'} · {dt(a.uploaded_at)}</div>
                    </div>
                    {a.file_url && <a href={a.file_url} target="_blank" rel="noreferrer" className="acct-btn acct-btn-secondary" style={{ padding: '4px 10px', fontSize: '.7rem' }}>View</a>}
                  </div>
                ))}
              </div>
            )}
          </div>}
        </div>
      </div>
    </div>

    {/* Pay Bill Modal */}
    {payModal && <div className="acct-modal-overlay" onClick={() => setPayModal(false)}>
      <div className="acct-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header"><h3 className="acct-modal-title">Pay Supplier Bill</h3>
          <button className="acct-modal-close" onClick={() => setPayModal(false)}>✕</button></div>
        <div className="acct-modal-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="acct-form-group"><label className="acct-form-label">Outstanding Due</label>
              <input className="acct-form-input" value={money(due)} readOnly style={{ fontWeight: 700, color: '#dc2626' }} /></div>
            <div className="acct-form-group"><label className="acct-form-label">Amount *</label>
              <input className="acct-form-input" type="number" min="0.01" max={due} value={payForm.amount || ''} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} /></div>
            <div className="acct-form-group"><label className="acct-form-label">Payment Account *</label>
              <SearchableSelect value={payForm.method || ''} onChange={n => setPayForm(f => ({ ...f, method: n }))} placeholder="Select method"
                options={paymentAccountOptions(scopedMethods.length ? scopedMethods : paymentMethods)} /></div>
            {payForm.method && <div className="acct-form-group"><label className="acct-form-label">Source CashBankAccount</label>
              <input className="acct-form-input" value={methodAccount(payForm.method)} readOnly /></div>}
            <div className="acct-form-group"><label className="acct-form-label">Reference</label>
              <input className="acct-form-input" value={payForm.reference || ''} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} placeholder="Optional" /></div>
            <div className="acct-form-actions">
              <button className="acct-btn acct-btn-secondary" onClick={() => setPayModal(false)}>Cancel</button>
              <button className="acct-btn acct-btn-primary" onClick={handlePay} disabled={paying || !payForm.method || !payForm.amount}>{paying ? 'Paying…' : 'Record Payment'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>}

    {/* Return Stock Modal */}
    {returnModal && <div className="acct-modal-overlay" style={{ zIndex: 10001 }} onClick={() => setReturnModal(false)}>
      <div className="acct-modal" style={{ maxWidth: 760, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header">
          <div>
            <h3 className="acct-modal-title">Return Items to Supplier</h3>
            <span style={{ fontSize: '.75rem', color: '#64748b' }}>{detail.supplier_name} {detail.supplier_phone ? `· ${detail.supplier_phone}` : ''} · {detail.purchase_number}</span>
          </div>
          <button className="acct-modal-close" onClick={() => setReturnModal(false)}><X size={16} /></button>
        </div>
        <div className="acct-modal-body">
          <div style={{ fontSize: '.75rem', color: '#475569', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
            This reduces the supplier payable first; the already-paid portion becomes a <strong>Supplier Receivable (1300)</strong>. No cash/bank account is involved.
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
              <thead><tr style={{ background: '#f8fafc' }}>
                <th style={thStyle}>Item</th><th style={thStyle}>Purchased</th><th style={thStyle}>Already Returned</th><th style={thStyle}>Remaining</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Unit Cost</th><th style={thStyle}>Return Qty</th><th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
              </tr></thead>
              <tbody>{(detail.items || []).map((i: any) => {
                const maxQty = Math.max(0, Number(i.quantity || 0) - (returnedMap[i.id] || 0));
                return <tr key={i.id}>
                  <td style={tdStyle}><strong>{i.product_name}</strong><br /><span style={{ color: '#64748b', fontSize: '.68rem' }}>{i.color || ''} {i.size || ''} · {i.sku || ''}</span></td>
                  <td style={tdStyle}>{i.quantity}</td>
                  <td style={tdStyle}>{returnedMap[i.id] || 0}</td>
                  <td style={tdStyle}>{maxQty}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{money(i.unit_cost)}</td>
                  <td style={tdStyle}><input type="number" min={0} max={maxQty}
                    value={returnItems[i.id] || 0}
                    onChange={e => setReturnItems(r => ({ ...r, [i.id]: Math.min(Math.max(0, Number(e.target.value) || 0), maxQty) }))}
                    style={{ width: 70, border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 6px', fontSize: '.78rem' }} />
                    {maxQty <= 0 && <span style={{ fontSize: '.65rem', color: '#dc2626' }}> fully returned</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{money(Number(returnItems[i.id] || 0) * Number(i.unit_cost || 0))}</td>
                </tr>;
              })}</tbody>
            </table>
            <div style={{ padding: '6px 10px', fontSize: '.68rem', color: '#94a3b8' }}>
              Unit cost is taken from the original purchase — it cannot be edited for purchase-based returns.
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div className="acct-form-group" style={{ margin: 0 }}><label className="acct-form-label">Return Reason *</label>
              <SearchableSelect value={returnReasonId} onChange={n => setReturnReasonId(String(n || ''))} placeholder="Select a reason"
                options={returnReasons.map((r: any) => ({ value: r.id, label: r.name }))} /></div>
            <div className="acct-form-group" style={{ margin: 0 }}><label className="acct-form-label">Notes</label>
              <input className="acct-form-input" value={returnNotes} onChange={e => setReturnNotes(e.target.value)} placeholder="Optional" /></div>
          </div>

          {previewing && <div style={{ fontSize: '.75rem', color: '#64748b', padding: '8px 10px' }}>Calculating accounting impact…</div>}

          {preview && !previewing && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: '.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: '#a16207', marginBottom: 6 }}>Accounting Impact — computed by backend</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: '.78rem', marginBottom: 8 }}>
                <span>Inventory reduction: <strong style={{ color: '#dc2626' }}>{money(preview.inventory_reduction)}</strong></span>
                <span>Payable reduction: <strong style={{ color: '#15803d' }}>{money(preview.payable_reduction)}</strong></span>
                <span>Supplier receivable: <strong style={{ color: '#15803d' }}>{money(preview.supplier_receivable)}</strong></span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
                <thead><tr style={{ background: '#fef3c7' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 700 }}>Side</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 700 }}>Account</th>
                  <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700 }}>Amount</th>
                </tr></thead>
                <tbody>{(preview.journal_lines || []).map((l: any, idx: number) => (
                  <tr key={idx}>
                    <td style={{ padding: '5px 8px', fontWeight: 700, color: l.side === 'Dr' ? '#b91c1c' : '#15803d', borderTop: '1px solid #fde68a' }}>{l.side}</td>
                    <td style={{ padding: '5px 8px', borderTop: '1px solid #fde68a' }}>{l.account_code} — {l.account_name}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', borderTop: '1px solid #fde68a' }}>{money(l.amount)}</td>
                  </tr>
                ))}</tbody>
              </table>
              <div style={{ marginTop: 6, fontSize: '.66rem', color: '#a16207' }}>
                No PaymentTransaction, cash transfer, or cash-account selection is created. Cash moves only when the supplier actually settles.
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: 8, padding: '10px 12px' }}>
            <span style={{ fontSize: '.8rem', color: '#64748b' }}>Total return value</span>
            <strong style={{ fontSize: '1rem', color: '#d97706' }}>{money(returnTotal)}</strong>
          </div>
          <div className="acct-form-actions" style={{ marginTop: 16 }}>
            <button className="acct-btn acct-btn-secondary" onClick={() => setReturnModal(false)}>Cancel</button>
            <button className="acct-btn acct-btn-success" onClick={postReturn} disabled={returning || returnTotal <= 0 || !returnReasonId}
              style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: (returning || returnTotal <= 0 || !returnReasonId) ? .5 : 1, cursor: (returning || returnTotal <= 0 || !returnReasonId) ? 'not-allowed' : 'pointer' }}>
              <RotateCcw size={14} /> {returning ? 'Posting…' : 'Post Return'}
            </button>
          </div>
        </div>
      </div>
    </div>}
  </>;
}

/* ══════════════════════════ ADD PURCHASE WORKSPACE ══════════════════════════ */

function AddPurchaseWorkspace({ locations, suppliers, locationFilter }: any) {
  const [toast, setToast] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});

  // Supplier search + context
  const [supplierQuery, setSupplierQuery] = useState('');
  const [supplierResults, setSupplierResults] = useState<any[]>([]);
  const [showSupplierResults, setShowSupplierResults] = useState(false);
  const [highlightedSupplier, setHighlightedSupplier] = useState(-1);
  const supplierSearchRef = useRef<HTMLDivElement>(null);
  const [supplierContext, setSupplierContext] = useState<any>(null);
  const [contextLoading, setContextLoading] = useState(false);

  // Product / variant search
  const [productQuery, setProductQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchEntry[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [highlightedSearchIdx, setHighlightedSearchIdx] = useState(-1);
  const [searchLoading, setSearchLoading] = useState(false);
  const productSearchRef = useRef<HTMLDivElement>(null);
  const productInputRef = useRef<HTMLInputElement>(null);
  const searchDebounce = useRef<any>(null);

  // Invoice rows
  const [invoiceRows, setInvoiceRows] = useState<InvoiceRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [collapsedProducts, setCollapsedProducts] = useState<Set<number>>(new Set());
  const [allCollapsed, setAllCollapsed] = useState(false);

  // Bulk update
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ unit_cost: '', wholesale_price: '', quantity: '' });
  const [bulkPreview, setBulkPreview] = useState<{ key: string; label: string; after: string }[]>([]);

  // Destination stock map for current-stock column
  const [stockMap, setStockMap] = useState<Record<string, number>>({});
  const [loadingStock, setLoadingStock] = useState(false);

  // Cash/bank accounts for the paid amount — filtered by the global location filter
  const [cashAccounts, setCashAccounts] = useState<any[]>([]);
  useEffect(() => {
    erpAPI.list('accounts-section/cash-bank', locationFilter ? { location: locationFilter } : undefined)
      .then(r => setCashAccounts(r.data?.accounts || []))
      .catch(() => setCashAccounts([]));
  }, [locationFilter]);

  const set = (key: string, value: any) => setForm(p => ({ ...p, [key]: value }));

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (productSearchRef.current && !productSearchRef.current.contains(e.target as Node)) setShowSearchResults(false);
      if (supplierSearchRef.current && !supplierSearchRef.current.contains(e.target as Node)) setShowSupplierResults(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Load destination stock when destination changes
  useEffect(() => {
    if (!form.destination) { setStockMap({}); return; }
    setLoadingStock(true);
    erpAPI.list('inventory', { location: form.destination, page_size: 500 })
      .then(r => {
        const map: Record<string, number> = {};
        rows(r.data).forEach((s: any) => { map[s.product_variant] = s.available_quantity ?? s.quantity; });
        setStockMap(map);
      }).catch(() => {}).finally(() => setLoadingStock(false));
  }, [form.destination]);

  // ── Supplier ──
  const filteredSuppliers = useMemo(() => {
    const q = supplierQuery.toLowerCase().trim();
    if (!q) return suppliers.slice(0, 10);
    return suppliers.filter((s: any) => s.name.toLowerCase().includes(q) || s.phone.includes(q)).slice(0, 10);
  }, [supplierQuery, suppliers]);

  const selectSupplier = (s: any) => {
    set('supplier', s.id);
    setSupplierQuery(`${s.name} (${s.phone})`);
    setShowSupplierResults(false);
    setHighlightedSupplier(-1);
    setSupplierContext(null);
    setContextLoading(true);
    erpAPI.list('purchases/supplier_context', { supplier: s.id })
      .then(r => setSupplierContext(r.data))
      .catch(() => {})
      .finally(() => setContextLoading(false));
  };

  const handleSupplierKeyDown = (e: React.KeyboardEvent) => {
    if (!showSupplierResults || !filteredSuppliers.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedSupplier(p => (p < filteredSuppliers.length - 1 ? p + 1 : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedSupplier(p => (p > 0 ? p - 1 : filteredSuppliers.length - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (highlightedSupplier >= 0) selectSupplier(filteredSuppliers[highlightedSupplier]); }
    else if (e.key === 'Escape') { e.preventDefault(); setShowSupplierResults(false); setHighlightedSupplier(-1); }
  };

  // ── Product / variant search ──
  const [localProducts, setLocalProducts] = useState<any[]>([]);
  useEffect(() => {
    erpAPI.list('products', { page_size: 200 }).then(r => setLocalProducts(rows(r.data))).catch(() => {});
  }, []);

  const localProductEntries = useMemo(() => {
    const text = productQuery.toLowerCase().trim();
    if (!text) return [];
    return localProducts.filter((p: any) =>
      p.name_en?.toLowerCase().includes(text) || p.name_bn?.toLowerCase().includes(text) || p.sku?.toLowerCase().includes(text)
    ).slice(0, 5).map((p: any) => ({ _type: 'product' as const, id: p.id, name_en: p.name_en, name_bn: p.name_bn, sku: p.sku }));
  }, [productQuery, localProducts]);

  const doVariantSearch = useCallback(async (q: string) => {
    if (q.trim().length < 1) return [];
    try {
      const res = await erpAPI.list('variants/search-for-purchase', { q: q.trim() });
      const list: any[] = Array.isArray(res.data) ? res.data : [];
      return list.slice(0, 8).map((v: any) => ({
        _type: 'variant' as const,
        id: v.id, product: v.product, product_name: v.product_name,
        product_code: v.product_code, sku: v.sku, color: v.color, size: v.size,
        unit_cost: v.unit_cost, wholesale_price: v.wholesale_price, selling_price: v.selling_price,
        current_stock: v.current_stock ?? null, last_purchase_cost: v.last_purchase_cost ?? null,
      }));
    } catch { return []; }
  }, []);

  const onProductQueryChange = (value: string) => {
    setProductQuery(value);
    setHighlightedSearchIdx(-1);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      setSearchLoading(true);
      const variants = await doVariantSearch(value);
      const combined: SearchEntry[] = [...localProductEntries, ...variants];
      setSearchResults(combined);
      setShowSearchResults(combined.length > 0);
      setSearchLoading(false);
    }, 250);
  };

  const rowKey = (product: number, color: string, size: string) => `${product}_${color}_${size}`;

  const mergeRow = (prev: InvoiceRow[], row: InvoiceRow) => {
    const existing = prev.find(r => r.key === row.key);
    if (existing) {
      setToast({ type: 'success', text: `+1 ${row.sku || row.product_name} — qty now ${existing.quantity + 1}` });
      return prev.map(r => r.key === row.key ? { ...r, quantity: r.quantity + 1 } : r);
    }
    setToast({ type: 'success', text: `Added ${row.sku || row.product_name}` });
    return [...prev, row];
  };

  const addVariantsFromProduct = async (productId: number, productName: string) => {
    try {
      const res = await erpAPI.create('purchases/draft-variants', { product: productId });
      const variants: any[] = Array.isArray(res.data) ? res.data : [];
      let added = 0;
      setInvoiceRows(prev => {
        let next = [...prev];
        for (const v of variants) {
          const row: InvoiceRow = {
            key: rowKey(v.product, v.color, v.size),
            product_variant: v.product_variant || undefined,
            product: v.product, product_name: v.product_name,
            product_code: v.product_code, sku: v.sku,
            color: v.color, size: v.size, brand: null, quantity: 1,
            unit_cost: v.unit_cost || 0, wholesale_price: v.wholesale_price || 0,
            selling_price: v.selling_price || 0,
            current_stock: v.product_variant ? stockMap[v.product_variant] ?? null : null,
          };
          const exists = next.some(r => r.key === row.key);
          if (exists) { next = next.map(r => r.key === row.key ? { ...r, quantity: r.quantity + 1 } : r); added++; }
          else { next = [...next, row]; added++; }
        }
        return next;
      });
      setToast({ type: 'success', text: added ? `Added ${added} variant(s) from ${productName}` : 'All variants already in the invoice.' });
    } catch (e) { setToast({ type: 'error', text: errorText(e) }); }
  };

  const addSingleVariant = (entry: SearchEntry & { _type: 'variant' }) => {
    const key = rowKey(entry.product, entry.color, entry.size);
    setInvoiceRows(prev => mergeRow(prev, {
      key, product_variant: entry.id,
      product: entry.product, product_name: entry.product_name,
      product_code: entry.product_code, sku: entry.sku,
      color: entry.color, size: entry.size, brand: null, quantity: 1,
      unit_cost: entry.unit_cost || 0, wholesale_price: entry.wholesale_price || 0,
      selling_price: entry.selling_price || 0,
      current_stock: stockMap[entry.id] ?? entry.current_stock ?? null,
      last_purchase_cost: entry.last_purchase_cost ?? null,
    }));
  };

  const onSelectSearchEntry = async (entry: SearchEntry) => {
    if (entry._type === 'product') {
      await addVariantsFromProduct(entry.id, entry.name_en);
    } else {
      addSingleVariant(entry);
    }
    setHighlightedSearchIdx(-1);
    setProductQuery('');
    setTimeout(() => productInputRef.current?.focus(), 50);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (!showSearchResults || !searchResults.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedSearchIdx(p => (p < searchResults.length - 1 ? p + 1 : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedSearchIdx(p => (p > 0 ? p - 1 : searchResults.length - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (highlightedSearchIdx >= 0) onSelectSearchEntry(searchResults[highlightedSearchIdx]); }
    else if (e.key === 'Escape') { e.preventDefault(); setShowSearchResults(false); setHighlightedSearchIdx(-1); }
  };

  // ── Row editing ──
  const updateRow = (key: string, field: keyof InvoiceRow, value: any) =>
    setInvoiceRows(current => current.map(row =>
      row.key === key ? { ...row, [field]: ['quantity', 'unit_cost', 'wholesale_price', 'selling_price'].includes(field) ? +value : value } : row
    ));

  const deleteRow = (key: string) => {
    setInvoiceRows(current => current.filter(x => x.key !== key));
    setSelectedRowKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
  };

  const deleteSelectedRows = () => {
    const count = selectedRowKeys.size;
    if (!count) return;
    setInvoiceRows(current => current.filter(x => !selectedRowKeys.has(x.key)));
    setSelectedRowKeys(new Set());
    setToast({ type: 'success', text: `Deleted ${count} row${count > 1 ? 's' : ''}.` });
  };

  const toggleRow = (key: string) => setSelectedRowKeys(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // ── Product-level selection ──
  const productSelectionState = (rows: InvoiceRow[]): 'all' | 'some' | 'none' => {
    if (!rows.length) return 'none';
    const selected = rows.filter(r => selectedRowKeys.has(r.key)).length;
    if (selected === rows.length) return 'all';
    if (selected > 0) return 'some';
    return 'none';
  };

  const toggleProduct = (rows: InvoiceRow[]) => {
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      const allSelected = rows.every(r => next.has(r.key));
      if (allSelected) {
        rows.forEach(r => next.delete(r.key));
      } else {
        rows.forEach(r => next.add(r.key));
      }
      return next;
    });
  };

  // ── Select-all across every variant ──
  const allSelectionState = (): 'all' | 'some' | 'none' => {
    if (!invoiceRows.length) return 'none';
    const selected = invoiceRows.filter(r => selectedRowKeys.has(r.key)).length;
    if (selected === invoiceRows.length) return 'all';
    if (selected > 0) return 'some';
    return 'none';
  };

  const toggleAll = () => {
    setSelectedRowKeys(prev => {
      const allSelected = invoiceRows.every(r => prev.has(r.key));
      return allSelected ? new Set() : new Set(invoiceRows.map(r => r.key));
    });
  };

  // ── Collapse / expand all product groups ──
  const toggleAllGroups = () => {
    if (allCollapsed) {
      setAllCollapsed(false);
      setCollapsedProducts(new Set());
    } else {
      setAllCollapsed(true);
    }
  };

  // ── Bulk update ──
  const openBulk = () => {
    const keys = selectedRowKeys.size ? selectedRowKeys : new Set(invoiceRows.map(r => r.key));
    const preview = invoiceRows.filter(r => keys.has(r.key)).map(r => {
      const label = [r.product_name, r.color, r.size].filter(Boolean).join(' - ') || r.sku;
      const parts: string[] = [];
      if (bulkForm.quantity !== '') parts.push(`Qty ${r.quantity} → ${parseInt(bulkForm.quantity) || 1}`);
      if (bulkForm.unit_cost !== '') parts.push(`Cost ${money(r.unit_cost)} → ${money(parseFloat(bulkForm.unit_cost))}`);
      if (bulkForm.wholesale_price !== '') parts.push(`Whole ${money(r.wholesale_price)} → ${money(parseFloat(bulkForm.wholesale_price))}`);
      return { key: r.key, label, after: parts.join(' · ') };
    });
    setBulkPreview(preview);
    setBulkModalOpen(true);
  };

  const applyBulk = () => {
    const keys = selectedRowKeys.size ? selectedRowKeys : new Set(invoiceRows.map(r => r.key));
    setInvoiceRows(current => current.map(row => {
      if (!keys.has(row.key)) return row;
      const updated = { ...row };
      if (bulkForm.unit_cost !== '') updated.unit_cost = parseFloat(bulkForm.unit_cost);
      if (bulkForm.wholesale_price !== '') updated.wholesale_price = parseFloat(bulkForm.wholesale_price);
      if (bulkForm.quantity !== '') updated.quantity = parseInt(bulkForm.quantity) || 1;
      return updated;
    }));
    setBulkModalOpen(false);
    setBulkForm({ unit_cost: '', wholesale_price: '', quantity: '' });
    setToast({ type: 'success', text: 'Bulk update applied.' });
  };

  // ── Totals ──
  const total = invoiceRows.reduce((sum, row) => sum + row.quantity * row.unit_cost, 0);
  const totalPaid = +(form.paid_amount || 0);
  const totalDue = Math.max(0, total - totalPaid);
  const totalVariants = invoiceRows.length;

  // Group by product
  const groupedRows = useMemo(() => {
    const groups: { productName: string; rows: InvoiceRow[] }[] = [];
    for (const name of [...new Set(invoiceRows.map(r => r.product_name))]) {
      groups.push({ productName: name, rows: invoiceRows.filter(r => r.product_name === name) });
    }
    return groups;
  }, [invoiceRows]);

  // ── Submit ──
  const submit = async () => {
    if (!form.supplier || !form.destination || !invoiceRows.length) {
      setToast({ type: 'warning', text: 'Select supplier and destination, then add at least one product.' }); return;
    }
    if (+form.paid_amount > 0 && !form.cash_bank_account) {
      setToast({ type: 'warning', text: 'Select the cash/bank account the paid amount is deducted from.' }); return;
    }
    const bad = invoiceRows.find(row => row.quantity < 1 || row.unit_cost < 0 || row.wholesale_price < 0 || row.selling_price < 0);
    if (bad) {
      setToast({ type: 'warning', text: `Row "${[bad.product_name, bad.color, bad.size].filter(Boolean).join(' - ')}" needs a positive quantity and valid prices.` }); return;
    }
    setSaving(true);
    try {
      const payload: any = {
        supplier: +form.supplier, destination: +form.destination,
        invoice_number: form.invoice_number || '', purchase_date: form.purchase_date || undefined,
        paid_amount: +form.paid_amount || 0, notes: form.notes || '',
        cash_bank_account: form.cash_bank_account ? +form.cash_bank_account : null,
        items: invoiceRows.map(({ key, product_name, product_code, current_stock, last_purchase_cost, ...rest }) => ({ ...rest, product_variant: rest.product_variant || null })),
      };
      await erpAPI.create('purchases', payload);
      setInvoiceRows([]); setForm({}); setSelectedRowKeys(new Set());
      setSupplierQuery(''); setProductQuery(''); setSupplierContext(null);
      setToast({ type: 'success', text: 'Purchase posted. Inventory, payable, ledger, and journal were updated.' });
      productInputRef.current?.focus();
    } catch (e) {
      setToast({ type: 'error', text: errorText(e) });
    } finally { setSaving(false); }
  };

  return <div className="erp-page">
    {toast && <Toast show message={toast.text} type={toast.type} onClose={() => setToast(null)} />}

    {/* Purchase info bar */}
    <div className="pm-entry-bar">
      <div className="pm-entry-field pm-supplier-field" ref={supplierSearchRef}>
        <label>Supplier *</label>
        <input value={supplierQuery}
          onChange={e => { setSupplierQuery(e.target.value); set('supplier', ''); setSupplierContext(null); setShowSupplierResults(true); }}
          onFocus={() => setShowSupplierResults(true)}
          onKeyDown={handleSupplierKeyDown}
          placeholder="Search supplier by name or phone…" autoComplete="off" />
        {showSupplierResults && filteredSuppliers.length > 0 && (
          <div className="pm-dropdown">
            {filteredSuppliers.map((s: any, idx: number) => (
              <button key={s.id} type="button"
                className={`pm-option ${highlightedSupplier === idx ? 'pm-option-hl' : ''}`}
                onClick={() => selectSupplier(s)} onMouseEnter={() => setHighlightedSupplier(idx)}>
                <strong>{s.name}</strong><span>{s.phone}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="pm-entry-field">
        <label>Destination *</label>
        <SearchableSelect value={form.destination || ''} onChange={n => set('destination', n)} placeholder="Select warehouse / shop"
          options={locations.map((l: any) => ({ value: l.id, label: `${l.name} (${l.location_type})` }))} />
      </div>
      <div className="pm-entry-field">
        <label>Date</label>
        <input type="date" value={form.purchase_date || ''} onChange={e => set('purchase_date', e.target.value)} />
      </div>
      <div className="pm-entry-field">
        <label>Supplier Invoice</label>
        <input value={form.invoice_number || ''} onChange={e => set('invoice_number', e.target.value)} placeholder="Optional" />
      </div>
    </div>

    {/* Supplier context panel */}
    {form.supplier && (supplierContext || contextLoading) && (
      <div className="pm-supplier-context">
        {contextLoading ? <Loader2 size={14} className="spin" /> : <>
          <div className="pm-sc-title"><strong>{supplierContext.supplier?.name}</strong> <span>Supplier context</span></div>
          <div><small>Outstanding Payable</small><b style={{ color: supplierContext.outstanding_payable > 0 ? '#dc2626' : '#059669' }}>{money(supplierContext.outstanding_payable)}</b></div>
          <div><small>Previous Purchases</small><b>{supplierContext.previous_purchase_count}</b></div>
          <div><small>Last Purchase</small><b>{supplierContext.last_purchase_date ? date(supplierContext.last_purchase_date) : '—'}</b><em>{supplierContext.last_purchase_total != null ? money(supplierContext.last_purchase_total) : ''}</em></div>
          {(supplierContext.recent_purchase_prices || []).length > 0 && (
            <div className="pm-sc-prices"><small>Recent Purchase Prices</small>
              <div>{(supplierContext.recent_purchase_prices as any[]).slice(0, 4).map((p: any, i: number) => (
                <span key={i}>{[p.product_name, p.color, p.size].filter(Boolean).join(' - ') || p.sku}: {money(p.unit_cost)}</span>
              ))}</div>
            </div>
          )}
        </>}
      </div>
    )}

    {/* Product / variant search */}
    <div className="pm-product-bar">
      <div className="pm-product-search" ref={productSearchRef}>
        <Search size={16} style={{ color: '#94a3b8' }} />
        <input ref={productInputRef} type="text" value={productQuery}
          onChange={e => onProductQueryChange(e.target.value)}
          onFocus={() => { if (searchResults.length) setShowSearchResults(true); }}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search by barcode, SKU, product name, color, size…" autoComplete="off" />
        {(searchLoading || loadingStock) && <Loader2 size={16} className="spin" />}
        {showSearchResults && searchResults.length > 0 && (
          <div className="pm-product-results">
            {(() => {
              const productEntries = searchResults.filter(e => e._type === 'product');
              const variantEntries = searchResults.filter(e => e._type === 'variant');
              let globalIdx = 0;
              const sections: { label: string; items: SearchEntry[]; startIdx: number }[] = [];
              if (productEntries.length) { sections.push({ label: 'Products', items: productEntries, startIdx: globalIdx }); globalIdx += productEntries.length; }
              if (variantEntries.length) { sections.push({ label: 'Variants', items: variantEntries, startIdx: globalIdx }); globalIdx += variantEntries.length; }
              return sections.map(section => (
                <div key={section.label}>
                  <div className="pm-search-section-label">{section.label}</div>
                  {section.items.map((entry, localIdx) => {
                    const idx = section.startIdx + localIdx;
                    if (entry._type === 'product') {
                      return <button key={`p-${entry.id}`} type="button"
                        className={`pm-product-result ${highlightedSearchIdx === idx ? 'pm-option-hl' : ''}`}
                        onClick={() => onSelectSearchEntry(entry)} onMouseEnter={() => setHighlightedSearchIdx(idx)}>
                        <strong>{entry.name_en}</strong>
                        <span className="pm-vr-detail">{entry.sku} — click to add all variants</span>
                      </button>;
                    }
                    return <button key={`v-${entry.id}`} type="button"
                      className={`pm-variant-result ${highlightedSearchIdx === idx ? 'pm-option-hl' : ''}`}
                      onClick={() => onSelectSearchEntry(entry)} onMouseEnter={() => setHighlightedSearchIdx(idx)}>
                      <span className="pm-vr-product">{entry.product_name}</span>
                      <span className="pm-vr-detail">{entry.color}{entry.color && entry.size ? ' • ' : ''}{entry.size} — {entry.sku}{entry.current_stock != null ? ` · stock ${entry.current_stock}` : ''}</span>
                    </button>;
                  })}
                </div>
              ));
            })()}
          </div>
        )}
        {showSearchResults && !searchResults.length && productQuery.trim().length >= 1 && !searchLoading && (
          <div className="pm-product-results"><div className="pm-empty">No products or variants found</div></div>
        )}
      </div>
      {totalVariants > 0 && (
        <div className="pm-bar-actions">
          <button className="pm-btn pm-btn-secondary pm-btn-sm" onClick={openBulk}>Bulk Update</button>
          <span className="pm-count">{totalVariants} variant{totalVariants > 1 ? 's' : ''}</span>
        </div>
      )}
    </div>

    {/* Invoice table */}
    <section className="erp-card pm-card-table">
      <div className="erp-table-title">
        <h2 style={{ margin: 0 }}>Invoice Items</h2>
        <div className="pm-title-actions">
          {selectedRowKeys.size > 0 && (
            <button className="pm-btn pm-btn-danger pm-btn-sm" onClick={deleteSelectedRows}><Trash2 size={13} /> Delete selected ({selectedRowKeys.size})</button>
          )}
        </div>
      </div>
      {totalVariants === 0 ? (
        <div className="pm-empty pm-empty-box">
          <p>No items yet. Search and select a product or variant above to start the purchase.</p>
        </div>
      ) : (
        <div className="erp-table-wrap pm-table-wrap">
          <table>
            <colgroup>
              <col style={{ width: 44 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 40 }} />
            </colgroup>
            <thead><tr className="pm-header-toggle" onClick={toggleAllGroups}>
              <th style={{ width: 44 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="checkbox"
                    checked={allSelectionState() === 'all'}
                    ref={el => { if (el) el.indeterminate = allSelectionState() === 'some'; }}
                    onChange={toggleAll}
                    onClick={e => e.stopPropagation()}
                    title={allSelectionState() === 'all' ? 'Deselect all variants' : 'Select all variants'}
                  />
                  <span className="pm-col-toggle" role="button" onClick={e => { e.stopPropagation(); toggleAllGroups(); }} title={allCollapsed ? 'Expand all product groups' : 'Collapse all product groups'}>{allCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}</span>
                </div>
              </th>
              <th>Product</th><th>Color</th><th>Size</th><th>SKU</th><th className="pm-th-num">Current Stock</th>
              <th className="pm-th-num">Qty</th><th className="pm-th-num">Unit Cost</th><th className="pm-th-num">Wholesale</th><th className="pm-th-num">Selling <span className="pm-th-note">(from Product)</span></th><th className="pm-th-num">Line Total</th><th style={{ width: 40 }} />
            </tr></thead>
            <tbody>
              {groupedRows.map(group => {
                const pid = group.rows[0]?.product;
                const collapsed = allCollapsed || collapsedProducts.has(pid);
                return (
                  <React.Fragment key={group.productName}>
                    <tr className="pm-group-row" onClick={() => {
                      setAllCollapsed(false);
                      setCollapsedProducts(prev => {
                        const next = new Set(prev);
                        if (collapsed) next.delete(pid); else next.add(pid);
                        return next;
                      });
                    }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <input
                            type="checkbox"
                            checked={productSelectionState(group.rows) === 'all'}
                            ref={el => { if (el) el.indeterminate = productSelectionState(group.rows) === 'some'; }}
                            onChange={() => toggleProduct(group.rows)}
                            onClick={e => e.stopPropagation()}
                            title={productSelectionState(group.rows) === 'all' ? 'Deselect all variants of this product' : 'Select all variants of this product'}
                          />
                          <button className="pm-group-toggle" type="button">
                            {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                          </button>
                        </div>
                      </td>
                      <td colSpan={11}><strong>{group.productName}</strong> <span className="pm-count">{group.rows.length} variant{group.rows.length > 1 ? 's' : ''}</span></td>
                    </tr>
                    {!collapsed && group.rows.map(row => (
                      <tr key={row.key} className={selectedRowKeys.has(row.key) ? 'pm-row-selected' : ''}>
                        <td><input type="checkbox" checked={selectedRowKeys.has(row.key)} onChange={() => toggleRow(row.key)} /></td>
                        <td className="pm-prod-name" style={{ fontWeight: 500 }}>{row.product_name}</td>
                        <td>{row.color || '—'}</td>
                        <td>{row.size || '—'}</td>
                        <td><code>{row.sku}</code></td>
                        <td className="pm-td-num">
                          <span style={{ color: (row.current_stock ?? 0) <= 0 ? '#b45309' : '#475569', fontWeight: 600 }}>{row.current_stock != null ? row.current_stock : '—'}</span>
                          {row.last_purchase_cost != null && <small className="pm-last-cost">last {money(row.last_purchase_cost)}</small>}
                        </td>
                        <td className="pm-td-num"><input type="number" min="1" step="1" value={row.quantity} onChange={e => updateRow(row.key, 'quantity', e.target.value)} className="pm-qty-input" /></td>
                        <td className="pm-td-num"><input type="number" min="0" step=".01" value={row.unit_cost} onChange={e => updateRow(row.key, 'unit_cost', e.target.value)} className="pm-price-input" /></td>
                        <td className="pm-td-num"><input type="number" min="0" step=".01" value={row.wholesale_price} onChange={e => updateRow(row.key, 'wholesale_price', e.target.value)} className="pm-price-input" /></td>
                        <td className="pm-td-num"><div className="pm-sell-readonly" title="Selling price is set on the Add Product page">{(row.selling_price || 0) > 0 ? money(row.selling_price) : '—'}</div></td>
                        <td className="pm-td-num pm-line-total">{money(row.quantity * row.unit_cost)}</td>
                        <td><button className="pm-row-del" onClick={() => deleteRow(row.key)}><Trash2 size={13} /></button></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>

    {/* Bulk Update Modal */}
    <Modal isOpen={bulkModalOpen} onClose={() => { setBulkModalOpen(false); }} title="Bulk Update" size="medium">
      <div className="pm-bulk">
        <p className="pm-bulk-hint">Only filled fields will be applied to {selectedRowKeys.size || 'all'} selected row{selectedRowKeys.size !== 1 ? 's' : ''}.</p>
        <div className="pm-bulk-fields">
          <div className="pm-field"><label>Unit Cost</label><input type="number" min="0" step="1" value={bulkForm.unit_cost} onChange={e => setBulkForm(p => ({ ...p, unit_cost: e.target.value }))} placeholder="Keep current" /></div>
          <div className="pm-field"><label>Wholesale Price</label><input type="number" min="0" step="1" value={bulkForm.wholesale_price} onChange={e => setBulkForm(p => ({ ...p, wholesale_price: e.target.value }))} placeholder="Keep current" /></div>
          <div className="pm-field"><label>Quantity</label><input type="number" min="1" step="1" value={bulkForm.quantity} onChange={e => setBulkForm(p => ({ ...p, quantity: e.target.value }))} placeholder="Keep current" /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button className="pm-btn pm-btn-secondary pm-btn-sm" onClick={openBulk}>Preview affected</button>
        </div>
        {bulkPreview.length > 0 && (
          <div className="pm-bulk-preview">
            <strong>Affected variants ({bulkPreview.length})</strong>
            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
              {bulkPreview.map(p => <div key={p.key}><span>{p.label}</span><em>{p.after}</em></div>)}
            </div>
          </div>
        )}
        <div className="pm-modal-actions">
          <button className="pm-btn pm-btn-secondary" onClick={() => setBulkModalOpen(false)}>Cancel</button>
          <button className="pm-btn pm-btn-primary" onClick={applyBulk}>Apply</button>
        </div>
      </div>
    </Modal>

    {/* Sticky footer summary */}
    {totalVariants > 0 && (
      <div className="pm-footer-bar">
        <div className="pm-footer-left">
          <div className="pm-field-inline"><label>Paid now</label><input type="number" min="0" step="1" value={form.paid_amount || ''} onChange={e => set('paid_amount', e.target.value)} placeholder="0.00" /></div>
          {+form.paid_amount > 0 && (
            <div className="pm-field-inline pm-cash-field">
              <label>Deduct from (Cash/Bank)</label>
              <select value={form.cash_bank_account || ''} onChange={e => set('cash_bank_account', e.target.value)}>
                <option value="">Select account…</option>
                {cashAccounts.map((acc: any) => <option key={acc.id} value={acc.id}>{acc.account} — {acc.location_name}</option>)}
              </select>
            </div>
          )}
          <div className="pm-field-inline pm-field-notes"><label>Notes</label><textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Internal notes" rows={1.9} /></div>
        </div>
        <div className="pm-footer-right">
          <div className="pm-total-row">
            <span>Total <strong>{money(total)}</strong></span>
            <span>Paid <strong style={{ color: '#059669' }}>{money(totalPaid)}</strong></span>
            <span className="pm-due">Due <strong>{money(totalDue)}</strong></span>
            <button className="pm-btn pm-btn-primary pm-btn-post" disabled={saving || !totalVariants || !form.supplier || !form.destination || (+form.paid_amount > 0 && !form.cash_bank_account)} onClick={submit}>
              {saving ? <><Loader2 size={14} className="spin" /> Posting…</> : 'Post Purchase'}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>;
}

/* ══════════════════════════ SUPPLIERS WORKSPACE ══════════════════════════ */

function SuppliersWorkspace({ suppliers, onChanged }: { suppliers: any[]; onChanged: () => void }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<any>(null);
  const [query, setQuery] = useState('');

  const add = async () => {
    if (!form.name || !form.phone) { setToast({ type: 'warning', text: 'Supplier name and phone are required.' }); return; }
    setSaving(true);
    try {
      await erpAPI.create('suppliers', {
        name: form.name, phone: form.phone, contact_person: form.contact || '',
        email: form.email || '', address: form.address || '',
      });
      setForm({}); setModalOpen(false); setToast({ type: 'success', text: 'Supplier added.' }); onChanged();
    } catch (e) { setToast({ type: 'error', text: errorText(e) }); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return suppliers;
    return suppliers.filter((s: any) => s.name.toLowerCase().includes(q) || s.phone.includes(q));
  }, [query, suppliers]);

  return <div className="erp-page">
    {toast && <Toast show message={toast.text} type={toast.type} onClose={() => setToast(null)} />}
    <div className="erp-filterbar">
      <Search size={15} style={{ color: '#94a3b8' }} />
      <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search suppliers…" style={{ border: 0, background: 'transparent', flex: 1, outline: 'none' }} />
      <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={() => setModalOpen(true)}><Plus size={14} /> Add supplier</button>
    </div>
    <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="erp-table-wrap">
        <table>
          <thead><tr><th>Supplier</th><th>Phone</th><th>Contact</th><th>Email</th><th>Status</th></tr></thead>
          <tbody>{filtered.length === 0 ? <tr><td className="erp-empty" colSpan={5}>No suppliers found.</td></tr> :
            filtered.map((s: any) => <tr key={s.id}>
              <td><strong>{s.name}</strong></td><td>{s.phone}</td><td>{s.contact_person || '—'}</td><td>{s.email || '—'}</td>
              <td><span className="erp-status received">{s.is_active ? 'ACTIVE' : 'INACTIVE'}</span></td>
            </tr>)}</tbody>
        </table>
      </div>
    </section>

    <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Add supplier" size="medium">
      <div className="pm-supplier-form">
        <p>Phone number is required and must be unique.</p>
        <div className="pm-field"><label>Supplier name *</label><input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Business or supplier name" /></div>
        <div className="pm-field"><label>Phone number *</label><input value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="01XXXXXXXXX" /></div>
        <div className="pm-field"><label>Contact person</label><input value={form.contact || ''} onChange={e => setForm(p => ({ ...p, contact: e.target.value }))} /></div>
        <div className="pm-field"><label>Email</label><input type="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
        <div className="pm-field"><label>Address</label><textarea value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} rows={3} /></div>
        <div className="pm-modal-actions">
          <button className="pm-btn pm-btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="pm-btn pm-btn-primary" onClick={add} disabled={saving}>{saving ? 'Saving…' : <><Plus size={15} /> Add supplier</>}</button>
        </div>
      </div>
    </Modal>
  </div>;
}

/* ══════════════════════════ SHARED DRAWER TAB COMPONENTS ══════════════════════════ */

const thStyle: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #f1f5f9' };

function ItemsTab({ items }: { items: any[] }) {
  return <>
    <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Items</h4>
    {(!items || items.length === 0) ? (
      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No items loaded.</div>
    ) : (
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
          <thead><tr style={{ background: '#f8fafc' }}>
            <th style={thStyle}>Product</th><th style={thStyle}>Variant</th><th style={thStyle}>SKU</th><th style={thStyle}>Qty</th><th style={thStyle}>Unit Cost</th><th style={thStyle}>Wholesale</th><th style={thStyle}>Selling</th><th style={thStyle}>Total</th>
          </tr></thead>
          <tbody>{items.map((item: any, i: number) => <tr key={i}>
            <td style={{ ...tdStyle, fontWeight: 500 }}>{item.product_name}</td>
            <td style={tdStyle}>{[item.color, item.size].filter(Boolean).join(' / ') || 'Default'}</td>
            <td style={tdStyle}><code>{item.sku}</code></td>
            <td style={{ ...tdStyle, textAlign: 'center' }}>{item.quantity}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(item.unit_cost)}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(item.wholesale_price)}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(item.selling_price)}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{money(item.line_total)}</td>
          </tr>)}</tbody>
        </table>
      </div>
    )}
  </>;
}

function PricingTab({ items }: { items: any[] }) {
  const totals = (items || []).reduce((acc: any, item: any) => {
    acc.cost += Number(item.unit_cost || 0) * item.quantity;
    acc.wholesale += Number(item.wholesale_price || 0) * item.quantity;
    acc.selling += Number(item.selling_price || 0) * item.quantity;
    return acc;
  }, { cost: 0, wholesale: 0, selling: 0 });
  return <>
    <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Purchase Pricing</h4>
    {(!items || items.length === 0) ? (
      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No pricing data.</div>
    ) : (
      <>
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
            <thead><tr style={{ background: '#f8fafc' }}>
              <th style={thStyle}>Item</th><th style={thStyle}>Qty</th><th style={thStyle}>Unit Cost</th><th style={thStyle}>Wholesale</th><th style={thStyle}>Selling</th><th style={thStyle}>Cost Total</th>
            </tr></thead>
            <tbody>{items.map((item: any, i: number) => <tr key={i}>
              <td style={{ ...tdStyle, fontWeight: 500 }}>{[item.product_name, item.color, item.size].filter(Boolean).join(' - ')}</td>
              <td style={{ ...tdStyle, textAlign: 'center' }}>{item.quantity}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{money(item.unit_cost)}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{money(item.wholesale_price)}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{money(item.selling_price)}</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{money(item.unit_cost * item.quantity)}</td>
            </tr>)}</tbody>
          </table>
        </div>
        <div className="pm-pricing-totals">
          <div><strong>Cost Total</strong><b>{money(totals.cost)}</b></div>
          <div><strong>Wholesale Value</strong><b>{money(totals.wholesale)}</b></div>
          <div><strong>Selling Value</strong><b style={{ color: '#059669' }}>{money(totals.selling)}</b></div>
        </div>
      </>
    )}
  </>;
}

function TimelineTab({ entries }: { entries: any[] }) {
  return <>
    <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Purchase Timeline</h4>
    {(!entries || entries.length === 0) ? (
      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No timeline entries.</div>
    ) : (
      <div style={{ position: 'relative', paddingLeft: 20 }}>
        <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: '#e2e8f0' }} />
        {entries.map((entry: any, i: number) => <div key={i} style={{ position: 'relative', padding: '0 0 14px 16px' }}>
          <div style={{ position: 'absolute', left: -16, top: 4, width: 10, height: 10, borderRadius: '50%', background: '#4f46e5', border: '2px solid #eef2ff' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '.78rem' }}>{entry.action}</div>
              {entry.notes && <div style={{ fontSize: '.7rem', color: '#64748b', marginTop: 2 }}>{entry.notes}</div>}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '.65rem', color: '#94a3b8' }}>{dt(entry.timestamp)}</div>
              <div style={{ fontSize: '.65rem', color: '#64748b' }}>{entry.user_name || '-'}</div>
            </div>
          </div>
        </div>)}
      </div>
    )}
  </>;
}

function AccountingTab({ purchase }: { purchase: any }) {
  const entries: any[] = purchase.journal_entries || [];
  return <>
    <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Journal Entries</h4>
    {entries.length === 0 ? (
      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No journal entries.</div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map((je: any, i: number) => {
          const lines: any[] = je.lines || [];
          const debit = lines.reduce((s: number, l: any) => s + Number(l.debit || 0), 0);
          const credit = lines.reduce((s: number, l: any) => s + Number(l.credit || 0), 0);
          const balanced = Math.abs(debit - credit) < 0.005;
          return <div key={i} style={{ padding: '10px', background: '#f8fafc', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: '.78rem', fontFamily: 'monospace' }}>{je.entry_number}</div>
              <span className="erp-status" style={{ background: balanced ? '#dcfce7' : '#fee2e2', color: balanced ? '#166534' : '#991b1b' }}>
                {balanced ? 'Balanced' : 'Unbalanced'}
              </span>
            </div>
            <div style={{ fontSize: '.72rem', color: '#64748b', marginTop: 2 }}>{je.description}</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.72rem', marginTop: 6 }}>
              <thead><tr><th style={thStyle}>Account</th><th style={{ ...thStyle, textAlign: 'right' }}>Debit</th><th style={{ ...thStyle, textAlign: 'right' }}>Credit</th></tr></thead>
              <tbody>{lines.map((l: any, j: number) => <tr key={j}>
                <td style={tdStyle}>{l.account_code} — {l.account_name || l.account}</td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#dc2626' }}>{money(l.debit)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#059669' }}>{money(l.credit)}</td>
              </tr>)}</tbody>
            </table>
          </div>;
        })}
      </div>
    )}

    <h4 style={{ margin: '16px 0 8px', fontSize: '.85rem' }}>Inventory Impact</h4>
    {(!purchase.stock_movements || purchase.stock_movements.length === 0) ? (
      <div style={{ padding: 14, textAlign: 'center', color: '#94a3b8', fontSize: '.75rem' }}>No stock movements.</div>
    ) : (
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
          <thead><tr style={{ background: '#f8fafc' }}>
            <th style={thStyle}>Variant</th><th style={thStyle}>Location</th><th style={{ ...thStyle, textAlign: 'right' }}>Change</th><th style={{ ...thStyle, textAlign: 'right' }}>Balance After</th><th style={thStyle}>By</th><th style={thStyle}>When</th>
          </tr></thead>
          <tbody>{(purchase.stock_movements || []).map((m: any, i: number) => <tr key={i}>
            <td style={{ ...tdStyle, fontWeight: 500 }}>{m.product_name} · {m.variant_color || ''}{m.variant_size ? ' / ' + m.variant_size : ''}</td>
            <td style={tdStyle}>{m.location_name || '-'}</td>
            <td style={{ ...tdStyle, textAlign: 'right', color: m.quantity > 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>{m.quantity > 0 ? '+' : ''}{m.quantity}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}>{m.balance_after}</td>
            <td style={tdStyle}>{m.created_by_name || '-'}</td>
            <td style={tdStyle}>{dt(m.created_at)}</td>
          </tr>)}</tbody>
        </table>
      </div>
    )}
  </>;
}
